import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/auth';
import { api } from '../utils/api';
import { Zap, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
}

export default function Register() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterForm>();
  const password = watch('password', '');
  
  const registerMutation = useMutation({
    mutationFn: (data: RegisterForm) => api.post('/auth/register', { email: data.email, password: data.password }),
    onSuccess: (response) => {
      setAuth(response.data.token, response.data.user);
      toast.success('Account created successfully!');
      navigate('/');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Registration failed');
    },
  });
  
  const onSubmit = (data: RegisterForm) => {
    if (!agreed) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    registerMutation.mutate(data);
  };
  
  // Password strength check
  const passwordChecks = [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'Contains uppercase letter', valid: /[A-Z]/.test(password) },
    { label: 'Contains number', valid: /[0-9]/.test(password) },
  ];
  
  return (
    <div className="min-h-screen bg-[#0b0e11] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#12161c] to-[#0b0e11] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#0ecb81]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-[#f0b90b]/10 rounded-full blur-[80px]" />
        
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center shadow-lg glow-primary">
              <Zap className="h-7 w-7 text-[#0b0e11]" />
            </div>
            <span className="text-2xl font-bold text-white">The Continental</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Start your journey to<br />
            <span className="text-gradient">financial freedom</span>
          </h1>
          
          <p className="text-lg text-[#848e9c] max-w-md mb-8">
            Join thousands of traders who use The Continental to automate their strategies 
            and maximize returns.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#0ecb81]/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-[#0ecb81]" />
              </div>
              <span className="text-[#eaecef]">Zero trading fees for first 30 days</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#0ecb81]/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-[#0ecb81]" />
              </div>
              <span className="text-[#eaecef]">Access to 500+ trading pairs</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#0ecb81]/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-[#0ecb81]" />
              </div>
              <span className="text-[#eaecef]">Unlimited workflow automations</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#0ecb81]/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-[#0ecb81]" />
              </div>
              <span className="text-[#eaecef]">24/7 customer support</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center">
              <Zap className="h-6 w-6 text-[#0b0e11]" />
            </div>
            <span className="text-2xl font-bold text-white">HybridX</span>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Create your account</h2>
            <p className="text-[#848e9c]">Start trading and automating in minutes</p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#848e9c] mb-2">
                Email Address
              </label>
              <input
                type="email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }
                })}
                className={errors.email ? 'input-error' : 'input'}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-[#f6465d]">{errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#848e9c] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' }
                  })}
                  className={errors.password ? 'input-error pr-12' : 'input pr-12'}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#848e9c] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password strength */}
              {password && (
                <div className="mt-3 space-y-2">
                  {passwordChecks.map((check, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${check.valid ? 'bg-[#0ecb81]/20' : 'bg-white/5'}`}>
                        {check.valid && <Check className="h-3 w-3 text-[#0ecb81]" />}
                      </div>
                      <span className={check.valid ? 'text-[#0ecb81]' : 'text-[#848e9c]'}>{check.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#848e9c] mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                {...register('confirmPassword', { 
                  required: 'Please confirm your password',
                  validate: value => value === password || 'Passwords do not match'
                })}
                className={errors.confirmPassword ? 'input-error' : 'input'}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && (
                <p className="mt-1.5 text-xs text-[#f6465d]">{errors.confirmPassword.message}</p>
              )}
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-[#181a20] text-[#f0b90b] focus:ring-[#f0b90b]" 
              />
              <span className="text-sm text-[#848e9c]">
                I agree to the{' '}
                <Link to="/terms" className="text-[#f0b90b] hover:text-[#f8d12f]">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#f0b90b] hover:text-[#f8d12f]">Privacy Policy</Link>
              </span>
            </label>
            
            <button
              type="submit"
              disabled={registerMutation.isPending || !agreed}
              className="btn-primary btn-lg w-full"
            >
              {registerMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="spinner" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create account
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </button>
          </form>
          
          <p className="mt-8 text-center text-[#848e9c]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#f0b90b] hover:text-[#f8d12f] font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
